<script setup>
import {
  getCurrentBreakpoint
} from "@voix/composables/useBreakpoints";
import { useWindowSize } from "@vueuse/core";
import { computed } from "vue";
import VoixMediaArray from "./media-formats/VoixMediaArray.vue";
import VoixMediaBreakpoint from "./media-formats/VoixMediaBreakpoint.vue";
import VoixMediaField from "./media-formats/VoixMediaField.vue";
import VoixMediaString from "./media-formats/VoixMediaString.vue";
defineOptions({
  inheritAttrs: false
});
const props = defineProps({
  background: {
    type: Boolean,
    default: false
  },
  field: {
    type: [Object, String],
    default: null
  },
  breakpoint: {
    type: String,
    default: null
  },
  loading: {
    type: String,
    default: "lazy"
  },
  picture: {
    type: Boolean,
    default: false
  },
  debug: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits([
  "loading",
  "load"
]);
const { width: windowWidth } = useWindowSize();
const currentBreakpoint = computed(() => {
  return getCurrentBreakpoint(windowWidth.value);
});
const renderModel = computed(() => {
  const theField = props.field;
  if (theField && (typeof theField === "string" || typeof theField.value === "string"))
    return "String";
  if (props.breakpoint && typeof theField === "object")
    return "Breakpoint";
  if (Array.isArray(theField))
    return "Array";
  if (typeof theField === "object")
    return "Field";
  return void 0;
});
const isEnabled = computed(() => {
  if (typeof props.field.enabled !== "undefined")
    return props.field.enabled;
  return true;
});
function mediaLoaded(data) {
  emit("load", data);
}
</script>

<template>
  <template v-if="isEnabled">
    <div
      v-if="debug"
      class="space-y-4 mt-[25px] bg-white p-8 px-8 rounded-sm whitespace-pre-line break-words max-w-[100vw] overflow-x-scroll"
    >
      <ClientOnly>
        <h1 class="text-2xl">
          Media Debug
        </h1>
        <div>
          <strong>Render Model:</strong>
          <pre>{{ renderModel }}</pre>
        </div>
        <div>
          <strong>Current Window Breakpoint:</strong>
          <pre>{{ currentBreakpoint?.name }}</pre>
        </div>
      </ClientOnly>
    </div>

    <template v-if="renderModel === 'Field'">
      <VoixMediaField
        v-slot="slotProps"
        v-bind="$attrs"
        :current-breakpoint="currentBreakpoint?.name"
        :field="field"
        :background="background"
        :loading="loading"
        :debug="debug"
        @load="mediaLoaded"
      >
        <slot v-bind="slotProps" />
      </VoixMediaField>
    </template>
    <template v-if="renderModel === 'Breakpoint'">
      <VoixMediaBreakpoint
        v-slot="slotProps"
        v-bind="$attrs"
        :field="field"
        :background="background"
        :breakpoint="breakpoint"
        :loading="loading"
        :debug="debug"
        @load="mediaLoaded"
      >
        <slot v-bind="slotProps" />
      </VoixMediaBreakpoint>
    </template>
    <template v-else-if="renderModel === 'String'">
      <VoixMediaString
        v-slot="slotProps"
        v-bind="$attrs"
        :current-breakpoint="currentBreakpoint?.name"
        :field="field"
        :background="background"
        :breakpoint="breakpoint"
        :loading="loading"
        :debug="debug"
        @load="mediaLoaded"
      >
        <slot v-bind="slotProps" />
      </VoixMediaString>
    </template>
    <template v-if="renderModel === 'Array'">
      <VoixMediaArray
        v-slot="slotProps"
        v-bind="$attrs"
        :current-breakpoint="currentBreakpoint?.name"
        :field="field"
        :background="background"
        :loading="loading"
        :debug="debug"
        @load="mediaLoaded"
      >
        <slot v-bind="slotProps" />
      </VoixMediaArray>
    </template>
  </template>
</template>
